import type { TileContent } from '@features/instructors/types/tile.types';

import { CobaltLoaderCircular } from '@cae/cobalt-react/components/loader-circular';
import { CobaltTag } from '@cae/cobalt-react/components/tag';
import {
  CobaltClickableTile,
  CobaltTile,
} from '@cae/cobalt-react/components/tile';
import { CobaltIconInfoCircleSolid } from '@cae/cobalt-react/icons/solid/CobaltIconInfoCircleSolid';
import * as React from 'react';

import styles from './InstructorActionTile.module.css';

const InstructorActionTile = React.memo(
  ({
    count,
    description,
    showInfoIcon,
    showProcessingLoader,
    isLoading,
    onClick,
    type,
  }: Readonly<TileContent>): React.ReactElement => {
    const isReadonly = type === 'processing-requests';
    const TileComponent = isReadonly ? CobaltTile : CobaltClickableTile;
    return (
      <TileComponent
        className={`${styles.actionTile} elevation-03-raised`}
        data-testid="instructor-action-tile"
        onClick={isReadonly ? undefined : onClick}
      >
        <CobaltTag color="info" className={styles.actionTileTag} />
        <div className={styles.actionTileTextContent}>
          <div className={styles.actionTileHeader}>
            {isLoading ? (
              // This hardcoded Skeleton will have to be replaced with the proper
              // Cobalt Skeleton component once it's available.
              (<div
                className={styles.countSkeleton}
                data-testid="action-tile-count-skeleton"
                aria-hidden="true"
              />)
            ) : (
              <p
                className={`heading-06 ${styles.actionTileCount}`}
                data-testid="action-tile-count"
              >
                {count}
              </p>
            )}
            {showInfoIcon && (
              <CobaltIconInfoCircleSolid
                size="medium"
                className={styles.actionTileIcon}
                aria-label="Info"
              />
            )}
            {showProcessingLoader && (
              <div className={styles.actionTileLoadingHeader}>
                <p className={`${styles.actionTileLoadingText} body-01`}>
                  Processing...
                </p>
                <CobaltLoaderCircular />
              </div>
            )}
          </div>
          <p
            className={styles.actionTileDescription}
            data-testid="action-tile-description"
          >
            {description}
          </p>
        </div>
      </TileComponent>
    );
  }
);

InstructorActionTile.displayName = 'InstructorActionTile';
export { InstructorActionTile };
